import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { take } from 'lib/array/arrayUtils'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { rem } from 'polished'
import InlineGroup from 'components/Common/InlineGroup'
import CalendarSidebarDaysMonth from './CalendarSidebarDaysMonth'
import TextButton from 'components/Luxkit/Button/TextButton'
import IconButton from 'components/Luxkit/Button/IconButton'
import BodyText from 'components/Luxkit/Typography/BodyText'
import CalendarPriceBlurb from './CalendarPriceBlurb'

const Root = styled.div`
  width: 100%;
`

const Controls = styled.div`
  display: flex;

  > * + * {
    margin-left: ${rem(12)};
  }
`

const NextMontControls = styled(Controls)`
  margin-top: ${rem(16)};
`

const Calendar = styled.div`
  & + & {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

interface Props {
  className?: string;
  onDatesChange: (day: App.BedbankCalendarDay) => void;
  onBackToMonth: () => void;
  monthIndex: string;
  duration: number;
  fetching: boolean;
  flights: boolean;
  daysByMonth: App.BedbankCalendarMonth[];
  adultsNumber: number;
  roomsNumber: number;
  displayPricingAsPerPerson: boolean;
  showTaxExclusivePrices: boolean;
}

function CalendarSidebarDayPicker(props: Props) {
  const {
    className,
    monthIndex,
    daysByMonth,
    onDatesChange,
    onBackToMonth,
    duration,
    fetching,
    flights,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
    showTaxExclusivePrices,
  } = props
  const [monthsOnDisplay, setMonthsOnDisplay] = useState(2)
  const [monthOffset, setMonthOffset] = useState(daysByMonth.findIndex((entry) => entry.key === monthIndex))

  const onPrevious = useCallback(() => {
    setMonthOffset(monthOffset - 1)
    setMonthsOnDisplay(monthsOnDisplay + 1)
  }, [monthOffset, monthsOnDisplay])

  const onNext = useCallback(() => {
    setMonthsOnDisplay(monthsOnDisplay + 1)
  }, [monthsOnDisplay])

  const keyData = flights ? 'cheapestDayWithFlights' : 'cheapestDay'

  return (
    <Root className={className}>
      {take(daysByMonth, monthsOnDisplay, monthOffset).map((value, index) =>
        <Calendar key={value.key}>
          <InlineGroup vertical="center" horizontal="between">
            <BodyText variant="large" align="center" weight="bold">{value.month} {value.year}</BodyText>
            {index === 0 && <Controls>
              <TextButton
                kind="secondary"
                disabled={monthOffset === 0}
                onClick={onPrevious}
              >
                Previous month
              </TextButton>
              <IconButton
                kind="secondary"
                onClick={onBackToMonth}
                aria-label="Return to Yearly Calendar"
              >
                <LineCalendarIcon />
              </IconButton>
            </Controls>}
          </InlineGroup>
          {!!value[keyData] && <CalendarPriceBlurb price={value[keyData]?.price} propertyFees={value[keyData]?.propertyFees} flightPrice={value[keyData]?.flightPrice} displayPricingAsPerPerson={displayPricingAsPerPerson} showTaxExclusivePrices={showTaxExclusivePrices} adultsNumber={adultsNumber} />}
          <CalendarSidebarDaysMonth
            fetching={fetching}
            flights={flights}
            days={value.days}
            onDatesChange={onDatesChange}
            duration={duration}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            showTaxExclusivePrices={showTaxExclusivePrices}
            adultsNumber={adultsNumber}
            roomsNumber={roomsNumber}
          />
        </Calendar>,
      )}
      <NextMontControls>
        <TextButton
          kind="secondary"
          disabled={(monthsOnDisplay + monthOffset) >= daysByMonth.length}
          onClick={onNext}
        >
          Next month
        </TextButton>
        <IconButton
          kind="secondary"
          onClick={onBackToMonth}
          aria-label="Return to Yearly Calendar"
        >
          <LineCalendarIcon />
        </IconButton>
      </NextMontControls>
    </Root>
  )
}

export default CalendarSidebarDayPicker
